import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';
import Image from '../../atoms/image';
import type { CallToActionProps } from './call-to-action.types';
import { useMemo } from 'react';

/**
 * CallToAction Component
 *
 * A customizable button-like component with icon, text, and chevron indicator.
 * Perfect for navigation items, settings options, or action prompts.
 */
export const CallToAction = React.forwardRef<HTMLDivElement, CallToActionProps>(
    (
        {
            imageIcon,
            testId,
            textId, // Mobile alias for testId
            iconSize = 20,
            titleSize = 'large',
            descriptionSize = 'medium',
            children,
            descriptionText,
            className,
            descriptionClassName,
            onClick,
            eventCallBack, // Mobile alias for onClick
            borderRadiusImage = 10,
            borderRadiusBox = 0,
            imageSize = 30,
            fontColor = 'rgb(var(--color-primary-100))',
            fontColorTextDescription = 'rgb(var(--color-neutral-60))',
            backgroundColor = 'rgb(var(--color-soft-accent-container))',
            paddingHorizontal = 16,
            paddingVertical = 5,
            disabled = false,
            opacity = 1,
            shadow = false,
            gapContainer = 8,
            gapText = 4,
            numberOfLines = Infinity,
            trailingAction,
        },
        ref
    ) => {
        // Support both onClick (web) and eventCallBack (mobile) for compatibility
        const handleClick = onClick ?? eventCallBack;
        // Support both testId and textId for compatibility
        const dataTestId = testId ?? textId ?? 'call-to-action';

        const isImageUri = typeof imageIcon === 'string';
        const isText = typeof children === 'string';

        const titleSizeClasses = {
            small: 'text-sm',
            medium: 'text-base',
            large: 'text-lg',
        };

        const descriptionSizeClasses = {
            small: 'text-xs',
            medium: 'text-sm',
            large: 'text-base',
        };

        const getTruncateClass = () => {
            if (numberOfLines === 1) return 'truncate';
            if (numberOfLines < Infinity) return `line-clamp-${numberOfLines}`;
            return '';
        };

        const truncateClass = getTruncateClass();

        const chevronStyle = useMemo(() => ({ color: fontColor }), [fontColor]);

        return (
            <div
                ref={ref}
                data-testid={dataTestId}
                onClick={disabled ? undefined : handleClick}
                style={{
                    backgroundColor,
                    opacity,
                    borderRadius: borderRadiusBox,
                    paddingLeft: paddingHorizontal,
                    paddingRight: paddingHorizontal,
                    paddingTop: paddingVertical,
                    paddingBottom: paddingVertical,
                    gap: gapContainer,
                }}
                className={cn(
                    'flex items-center cursor-pointer',
                    'transition-all duration-200',
                    shadow && 'shadow-md',
                    disabled && 'opacity-50 cursor-not-allowed',
                    !disabled && 'hover:opacity-90 active:scale-[0.98]'
                )}
            >
                {/* Image Icon */}
                {isImageUri ? (
                    <div
                        style={{
                            width: imageSize,
                            height: imageSize,
                            borderRadius: borderRadiusImage,
                        }}
                        className="flex-shrink-0 overflow-hidden relative"
                    >
                        <Image src={imageIcon} alt="" fill className="object-cover" />
                    </div>
                ) : (
                    <div
                        style={{
                            width: imageSize,
                            height: imageSize,
                        }}
                        className="flex-shrink-0 flex items-center justify-center"
                    >
                        {imageIcon}
                    </div>
                )}

                {/* Text Content */}
                <div className="flex-1 min-w-0" style={{ gap: gapText }}>
                    {isText ? (
                        <>
                            <div
                                style={{ color: fontColor }}
                                className={cn('font-medium', titleSizeClasses[titleSize], truncateClass, className)}
                            >
                                {children}
                            </div>
                            {descriptionText && (
                                <div
                                    style={{ color: fontColorTextDescription }}
                                    className={cn(
                                        descriptionSizeClasses[descriptionSize],
                                        truncateClass,
                                        descriptionClassName
                                    )}
                                >
                                    {descriptionText}
                                </div>
                            )}
                        </>
                    ) : (
                        children
                    )}
                </div>

                {trailingAction ?? <ChevronRight size={iconSize} style={chevronStyle} />}
            </div>
        );
    }
);

CallToAction.displayName = 'CallToAction';
